import { useTranslation } from 'react-i18next';

import { AreaTrendChart, type AreaTrendFold } from '../../shared/charts/AreaTrendChart';
import type { TimeLanguage } from '../../shared/format/time';
import { SectionCard } from '../../shared/surface/SectionCard';
import type { WalletLedgerEntry } from './types';
import { toWalletBalanceSeries } from './walletBalanceSeries';

export function WalletBalanceChart({
  entries,
  language,
  totalEntryCount,
}: {
  readonly entries: readonly WalletLedgerEntry[];
  readonly language: TimeLanguage;
  /** The wallet projection's `entryCount`, a decimal string per the contract. Displayed, never parsed. */
  readonly totalEntryCount: string;
}) {
  const { t } = useTranslation();
  const { categories, series } = toWalletBalanceSeries(
    entries,
    { available: t('wallets.chart.available'), reserved: t('wallets.chart.reserved') },
    language,
  );
  // One wallet holds one currency, so there is nothing to sum across units and `cap` never invents an
  // "Other" series.
  const fold: AreaTrendFold = { kind: 'cap' };
  // The axis is shared by both series and carries no currency symbol; the exact amount with its currency
  // is in the tooltip and the accessible table.
  const compact = new Intl.NumberFormat(language === 'th' ? 'th-TH-u-nu-latn' : 'en-US-u-nu-latn', {
    compactDisplay: 'short',
    maximumFractionDigits: 1,
    notation: 'compact',
  });
  const title = t('wallets.chart.titleScoped', {
    shown: categories.length,
    total: totalEntryCount,
  });

  return (
    <SectionCard headingLevel="h3" subtitle={t('wallets.chart.disclaimer')} title={title}>
      {categories.length === 0 ? null : (
        <AreaTrendChart
          ariaLabel={title}
          categories={categories}
          fold={fold}
          formatValue={(value) => compact.format(value)}
          rowHeader={t('wallets.chart.period')}
          series={series}
          tableCaption={title}
        />
      )}
    </SectionCard>
  );
}
